import React, { useState, useEffect } from "react";
import { useAuth } from "../store/Auth";
import { useFormStep } from "../store/FormStepStore";
import { useInvoiceStore } from "../store/InvoiceStore";
import { number_format } from "../utils/number_format";
import { remita_public_key, remita_secret } from "../constants/keys";
import { Link, useNavigate } from "react-router-dom";
import { base, confirm_payment, remita_inline_url } from "../constants/urls";
import Swal from "sweetalert2";
import { vatPercentage } from "../constants/others";
import { useCompanyVehicleDetails } from "../store/CompanyVehicleStore";
import Summary from "./Summary";
import { useOwnerStore } from "../store/OwnerStore";
import Header from "../components/assist/Title";

export default function PrePayment() {
  const step = useFormStep((state) => state.step);
  const getAuth = useAuth((state) => state.auth);
  const invoice = useInvoiceStore((state) => state.invoiceDetails);
  const setInvoice = useInvoiceStore((state) => state.setInvoiceDetails);
  const [proceed_to_summary, setProceedToSummary] = useState(false);
  const { ownerDetails } = useOwnerStore();
  const clearVehicles = useCompanyVehicleDetails((state) => state.clearAllItems);
  const navigate = useNavigate();

  // Route Protection - Redirect if not authenticated or step is too low
  useEffect(() => {
    if ((getAuth.authenticated === false && getAuth.token === "") || step < 6) {
      navigate("/signup", { replace: true });
    }
  }, [getAuth, step, navigate]);

  // Import Remita library
  useEffect(() => {
    const script = document.createElement("script");
    script.src = remita_inline_url;
    script.async = false;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const makePayment = () => {
    var paymentEngine = window.RmPaymentEngine.init({
      key: remita_public_key,
      processRrr: true,
      email: invoice.email,
      customerId: invoice.email,
      firstName: invoice.firstName,
      lastName: invoice.lastName,
      narration: invoice.narration,
      extendedData: {
        customFields: [
          {
            name: "rrr",
            value: invoice.rrr,
          },
        ],
      },
      onSuccess: function (response) {
        // Confirm payment if it is successful
        let token = getAuth.token;
        let url = `${base}${confirm_payment}cmrinfo1/${invoice.transactionId}/${invoice.rrr}`;

        var headers = new Headers();
        headers.append("Authorization", "Bearer " + token);

        var requestOptions = {
          method: "GET",
          headers: headers,
          redirect: "follow",
        };

        fetch(url, requestOptions)
          .then((response) => response.json())
          .then((result) => {
            if (result.data?.Status === "01") {
              setInvoice({
                ...invoice,
                status: "Paid",
              });
              clearVehicles();
              setProceedToSummary(true); // Only proceed if payment is confirmed
            } else {
              Swal.fire({
                title: "Payment Verification Failed",
                text: "Your payment could not be verified at this time.",
                icon: "warning",
                confirmButtonText: "Ok",
                confirmButtonColor: "#1C89DE",
              });
            }
          })
          .catch((error) => {
            console.error("Verification error", error);
            Swal.fire({
              title: "Network Error",
              text: "Could not verify payment status. Please check your connection.",
              icon: "error",
              confirmButtonText: "Ok",
              confirmButtonColor: "#1C89DE",
            });
          });
      },
      onError: function (response) {
        console.error("Callback Error Response", response);
        Swal.fire({
          title: "Error Initiating Payment",
          text: "Please contact customer support if this persists.",
          icon: "error",
          confirmButtonText: "Ok",
          confirmButtonColor: "#1C89DE",
        });
      },
      onClose: function () {
        // Just notify the user they cancelled. Do NOT route to summary here.
        Swal.fire({
            title: "Payment Cancelled",
            text: "You have closed the payment gateway.",
            icon: "info",
            confirmButtonText: "Ok",
            confirmButtonColor: "#1C89DE",
        });
      },
    });
    
    paymentEngine.showPaymentWidget();
  };

  // Prevent rendering if being redirected
  if ((getAuth.authenticated === false && getAuth.token === "") || step < 6) {
    return null; 
  }

  return (
    <div>
      <Header />
      {!proceed_to_summary ? (
        <div className="px-4 pt-8 bg-gray-100 min-h-screen">
          <div className="flex justify-center items-center mb-10">
            <div className="w-full max-w-4xl">
              <div className="flex justify-between items-center bg-gray-700 px-4 py-3 rounded-t-lg mt-3 mb-3">
                <p className="text-lg font-bold font-sans text-gray-100">
                  Payment Invoice
                </p>
                <Link
                  to={"/invoice/" + invoice.transactionId}
                  target="_self"
                  className="bg-blue-500 hover:bg-blue-600 transition-colors text-gray-100 rounded px-4 py-1.5 text-sm font-medium"
                >
                  View Invoice
                </Link>
              </div>

              <div className="md:flex gap-6 leading-relaxed lg:leading-loose bg-white p-6 rounded-b-lg shadow-sm">
                <div className="flex-1 space-y-2 mb-6 md:mb-0">
                  <div className="flex">
                    <div className="w-32 font-bold text-gray-700">Full Name:</div>
                    <div className="flex-1 text-gray-600">
                      {invoice.firstName} {invoice.middleName} {invoice.lastName}
                    </div>
                  </div>

                  <div className="flex">
                    <div className="w-32 font-bold text-gray-700">Address:</div>
                    <div className="flex-1 text-gray-600">{invoice.address}</div>
                  </div>

                  <div className="flex">
                    <div className="w-32 font-bold text-gray-700">Phone:</div>
                    <div className="flex-1 text-gray-600">{invoice.phone}</div>
                  </div>

                  <div className="flex">
                    <div className="w-32 font-bold text-gray-700">Email:</div>
                    <div className="flex-1 text-gray-600">{invoice.email}</div>
                  </div>
                </div>

                <div className="flex-1 border-2 border-solid border-red-800 p-5 rounded-lg bg-red-50/30">
                  <div className="space-y-2">
                    <div className="flex">
                      <div className="w-36 font-bold text-gray-800">Invoice No:</div>
                      <div className="flex-1 text-gray-700">CI{invoice.invoiceNumber}</div>
                    </div>
                    <div className="flex">
                      <div className="w-36 font-bold text-gray-800">Trans. Ref:</div>
                      <div className="flex-1 text-gray-700">{invoice.transactionId}</div>
                    </div>
                    <div className="flex">
                      <div className="w-36 font-bold text-gray-800">Narration:</div>
                      <div className="flex-1 text-gray-700">{invoice.narration}</div>
                    </div>
                    <div className="flex">
                      <div className="w-36 font-bold text-gray-800">Payment Ref:</div>
                      <div className="flex-1 font-bold text-gray-900">{invoice.rrr}</div>
                    </div>
                    <div className="flex items-center pt-2 mt-2 border-t border-red-200">
                      <div className="w-36 font-bold text-gray-900 text-lg">Total Amount:</div>
                      <div className="flex-1 font-bold text-red-600 text-lg">
                        ₦{number_format(
                          parseInt(invoice.amount) +
                            vatPercentage * parseInt(invoice.amount)
                        )}{" "}
                        <span className="text-xs text-gray-500 font-normal">(VAT Inc.)</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="w-full flex justify-end mt-6">
                    <button
                      onClick={makePayment}
                      className="rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors py-2.5 px-6 font-semibold text-white shadow-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      Make Payment
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <Summary owner_details={ownerDetails} invoice_details={invoice} />
      )}
    </div>
  );
}